import React, { useState, useEffect } from 'react';
import supabase from '../config/supabase';
import { directQuery } from '../utils/directFetch';

const InventoryViewer = ({ preLoadedData, initialViewMode, initialFilter, hideControls }) => {
  const [loading, setLoading] = useState(!preLoadedData);
  const [inventoryData, setInventoryData] = useState(preLoadedData || []);
  const [filteredData, setFilteredData] = useState([]);
  const [viewMode, setViewMode] = useState(initialViewMode || 'all'); // 'all', 'vendor', 'category', 'location'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState(initialFilter || '');
  const [currentStore, setCurrentStore] = useState(null);

  // Editing state
  const [editingCell, setEditingCell] = useState(null); // { itemId, field }
  const [editValue, setEditValue] = useState('');
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [saving, setSaving] = useState(false);

  // Selection state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  // Sort order state
  const [sortOrderMode, setSortOrderMode] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);

  // Load current user's store on component mount
  useEffect(() => {
    loadCurrentStore();
  }, []);

  // Handle prop updates
  useEffect(() => {
    if (initialViewMode) setViewMode(initialViewMode);
    if (initialFilter) setSelectedFilter(initialFilter);
  }, [initialViewMode, initialFilter]);

  // Handle preLoadedData updates
  useEffect(() => {
    if (preLoadedData) {
      setInventoryData(preLoadedData);
      setLoading(false);
      // Extract categories and locations from preLoadedData
      const uniqueCategories = [...new Set(preLoadedData.map(item => item.category).filter(Boolean))];
      const uniqueLocations = [...new Set(preLoadedData.map(item => item.location).filter(Boolean))];
      setCategories(uniqueCategories);
      setLocations(uniqueLocations);
    }
  }, [preLoadedData]);

  // Load inventory data and options (only if no preLoadedData)
  useEffect(() => {
    if (currentStore && !preLoadedData) {
      loadInventoryData();
      loadCategoriesAndLocations();
    }
  }, [currentStore, preLoadedData]); // Only reload when store changes and no preloaded data

  // Counter Intelligence (the chat drawer) changes the catalog through other
  // paths — archiving items from a review card, bulk edits, single-item creates,
  // price changes/updates, and chat imports — and dispatches
  // `counter-intelligence:applied` on window after each apply. Refetch on the
  // catalog-changing kinds so the grid reflects them without a manual refresh.
  // (Skipped when a parent feeds preLoadedData — that parent owns refreshing;
  // template/order/PAR kinds are dispatched too but don't change catalog rows.)
  useEffect(() => {
    if (preLoadedData) return undefined;
    const CATALOG_KINDS = new Set(['item_dedup', 'item_edit', 'item_create', 'price_change', 'price_update', 'item_import']);
    const handleAiApplied = (event) => {
      const kind = event && event.detail && event.detail.kind;
      if (!CATALOG_KINDS.has(kind)) return;
      loadInventoryData();
      loadCategoriesAndLocations();
    };
    window.addEventListener('counter-intelligence:applied', handleAiApplied);
    return () => window.removeEventListener('counter-intelligence:applied', handleAiApplied);
  }, [currentStore, preLoadedData]);

  // Filter data when view mode or search changes
  useEffect(() => {
    if (inventoryData.length > 0 || searchTerm || selectedFilter) {
      filterData();
    }
  }, [inventoryData, viewMode, searchTerm, selectedFilter]);

  const loadCurrentStore = async () => {
    try {
      console.log('🔍 [INVENTORY-VIEWER] Loading store from localStorage...');

      // Get store from localStorage (set by items.html auth handling)
      const storedStore = localStorage.getItem('currentStore');
      if (storedStore) {
        const store = JSON.parse(storedStore);
        console.log('✅ [INVENTORY-VIEWER] Found store in localStorage:', store.name);
        setCurrentStore(store);
      } else {
        console.log('⚠️ [INVENTORY-VIEWER] No store in localStorage, waiting for items.html auth...');

        // Wait for items.html to set localStorage
        setTimeout(() => {
          const store = JSON.parse(localStorage.getItem('currentStore') || 'null');
          if (store) {
            console.log('✅ [INVENTORY-VIEWER] Found store after delay:', store.name);
            setCurrentStore(store);
          } else {
            console.error('❌ [INVENTORY-VIEWER] Still no store found in localStorage');
            setLoading(false);
          }
        }, 1000);
      }
    } catch (error) {
      console.error('❌ [INVENTORY-VIEWER] Error loading current store:', error);
      setLoading(false);
    }
  };

  const loadInventoryData = async () => {
    if (!currentStore) return;

    console.log('Loading inventory for store:', currentStore);
    console.log('Store ID:', currentStore.id);

    try {
      // Load from inventory_items table using direct fetch (avoid hanging Supabase client)
      console.log('🔍 [INVENTORY-VIEWER] Querying inventory_items table with store:', currentStore.id);
      console.log('🔍 [DB-CALL-VIEWER] Starting inventory_items query for InventoryViewer, store:', currentStore.id);

      const { data: inventoryData, error: inventoryError } = await directQuery('inventory_items', {
        select: '*',
        eq: { store_id: currentStore.id },
        order: { column: 'created_at', ascending: false }
      });

      console.log('Inventory query result:', inventoryData);
      console.log('Inventory query error:', inventoryError);

      if (inventoryError) {
        console.error('Error loading inventory:', inventoryError);
        setInventoryData([]);
        return;
      }

      console.log('✅ [INVENTORY-VIEWER] Setting inventory data:', inventoryData?.length || 0, 'items via direct fetch');

      // Sort the data in JavaScript to handle nulls properly
      const sortedData = (inventoryData || []).sort((a, b) => {
        // First sort by location (nulls last)
        const locationA = a.location || 'zzz_no_location';
        const locationB = b.location || 'zzz_no_location';
        if (locationA !== locationB) {
          return locationA.localeCompare(locationB);
        }

        // Then by global_sort_order (nulls last)
        const sortA = a.global_sort_order || 999999;
        const sortB = b.global_sort_order || 999999;
        if (sortA !== sortB) {
          return sortA - sortB;
        }

        // Finally by created_at (newest first)
        return new Date(b.created_at) - new Date(a.created_at);
      });

      setInventoryData(sortedData);
    } catch (error) {
      console.error('Error loading inventory:', error);
      setInventoryData([]);
    } finally {
      setLoading(false);
    }
  };

  // Load unique categories and locations for dropdowns
  const loadCategoriesAndLocations = async () => {
    if (!currentStore) return;

    try {
      // Use direct fetch instead of hanging Supabase client
      const { data: inventoryData, error } = await directQuery('inventory_items', {
        select: 'category, location',
        eq: { store_id: currentStore.id }
      });

      if (!error && inventoryData) {
        const uniqueCategories = [...new Set(inventoryData.map(item => item.category).filter(Boolean))];
        const uniqueLocations = [...new Set(inventoryData.map(item => item.location).filter(Boolean))];

        setCategories(uniqueCategories);
        setLocations(uniqueLocations);
      }
    } catch (error) {
      console.error('Error loading categories and locations:', error);
    }
  };

  // Start editing a cell
  const startEditing = (itemId, field, currentValue) => {
    setEditingCell({ itemId, field });
    setEditValue(currentValue || '');
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Save the edited value
  const saveEdit = async (newValue = editValue) => {
    if (!editingCell) return;

    setSaving(true);
    try {
      const { itemId, field } = editingCell;

      // TODO: Convert to direct fetch for update operations
      console.log('⚠️ [INVENTORY-VIEWER] Individual item updates temporarily disabled to avoid hanging Supabase client');
      console.log('📝 [INVENTORY-VIEWER] Would update item', itemId, 'field', field, 'to', newValue);

      // For now, just show a message and update local state
      alert('Individual item updates temporarily disabled. Use bulk operations instead.');

      // Update local state
      setInventoryData(prev =>
        prev.map(item =>
          item.id === itemId
            ? { ...item, [field]: newValue }
            : item
        )
      );

      // If we added a new category or location, refresh the options
      if (field === 'category' && newValue && !categories.includes(newValue)) {
        setCategories(prev => [...prev, newValue]);
      }
      if (field === 'location' && newValue && !locations.includes(newValue)) {
        setLocations(prev => [...prev, newValue]);
      }

      cancelEditing();
    } catch (error) {
      console.error('Error saving edit:', error);
      alert('Error saving changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Render editable cell
  const renderEditableCell = (item, field, displayValue, isEditing) => {
    const fullText = String(displayValue || '-');
    if (!isEditing) {
      // Display mode - clickable
      return (
        <span
          onClick={() => startEditing(item.id, field, item[field])}
          style={{
            cursor: 'pointer',
            padding: '2px 4px',
            borderRadius: '3px',
            display: 'block',
            minWidth: '40px',
            minHeight: '16px',
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
          className="editable-cell"
          title={fullText}
        >
          {displayValue || '-'}
        </span>
      );
    }

    // Edit mode
    if (field === 'category') {
      return (
        <select
          value={editValue}
          onChange={(e) => {
            if (e.target.value === '__new__') {
              const newCategory = prompt('Enter new category name:');
              if (newCategory) {
                setEditValue(newCategory);
                saveEdit(newCategory);
              }
            } else {
              setEditValue(e.target.value);
            }
          }}
          onBlur={() => saveEdit()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveEdit();
            if (e.key === 'Escape') cancelEditing();
          }}
          autoFocus
          className="form-select form-select-sm"
          style={{ minWidth: '120px', fontSize: '0.75rem' }}
        >
          <option value="">Select category...</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
          <option value="__new__">+ Add new category</option>
        </select>
      );
    }

    if (field === 'location') {
      return (
        <select
          value={editValue}
          onChange={(e) => {
            if (e.target.value === '__new__') {
              const newLocation = prompt('Enter new location name:');
              if (newLocation) {
                setEditValue(newLocation);
                saveEdit(newLocation);
              }
            } else {
              setEditValue(e.target.value);
            }
          }}
          onBlur={() => saveEdit()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveEdit();
            if (e.key === 'Escape') cancelEditing();
          }}
          autoFocus
          className="form-select form-select-sm"
          style={{ minWidth: '120px', fontSize: '0.75rem' }}
        >
          <option value="">Select location...</option>
          {locations.map(loc => (
            <option key={loc} value={loc}>{loc}</option>
          ))}
          <option value="__new__">+ Add new location</option>
        </select>
      );
    }

    // Text input for other fields
    return (
      <input
        type={field.includes('price') || field.includes('qty') ? 'number' : 'text'}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={() => saveEdit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') saveEdit();
          if (e.key === 'Escape') cancelEditing();
        }}
        autoFocus
        className="form-control form-control-sm"
        style={{ minWidth: '80px', fontSize: '0.75rem' }}
        step={field.includes('price') ? '0.01' : '1'}
      />
    );
  };

  // Handle item selection
  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Handle select all
  const toggleSelectAll = (items) => {
    const allItemIds = items.map(item => item.id);
    const allSelected = allItemIds.every(id => selectedItems.has(id));

    if (allSelected) {
      // Deselect all
      setSelectedItems(new Set());
    } else {
      // Select all
      setSelectedItems(new Set(allItemIds));
    }
  };

  // Initialize sort orders for items that don't have them
  const initializeSortOrders = async (locationItems) => {
    const itemsNeedingOrder = locationItems.filter(item => !item.global_sort_order);

    if (itemsNeedingOrder.length === 0) return;

    try {
      // Find the highest existing sort order in this location
      const existingOrders = locationItems
        .map(item => item.global_sort_order)
        .filter(order => order != null)
        .sort((a, b) => a - b);

      let startOrder = existingOrders.length > 0 ? Math.max(...existingOrders) + 10 : 10;

      // Assign sort orders in increments of 10
      for (let i = 0; i < itemsNeedingOrder.length; i++) {
        const item = itemsNeedingOrder[i];
        const sortOrder = startOrder + (i * 10);

        // TODO: Convert to direct fetch for update operations
        console.log('⚠️ [INVENTORY-VIEWER] Sort order updates temporarily disabled');
        console.log('📝 [INVENTORY-VIEWER] Would update item', item.id, 'sort order to', sortOrder);
      }

      // Reload data to show new sort orders
      loadInventoryData();
    } catch (error) {
      console.error('Error initializing sort orders:', error);
    }
  };

  // Update sort order for an item
  const updateSortOrder = async (itemId, newSortOrder) => {
    try {
      // TODO: Convert to direct fetch for update operations
      console.log('⚠️ [INVENTORY-VIEWER] Individual sort order updates temporarily disabled');
      console.log('📝 [INVENTORY-VIEWER] Would update item', itemId, 'sort order to', newSortOrder);
      alert('Sort order updates temporarily disabled to avoid hanging database calls.');
      return false; // Indicate update failed

      // Update local state
      setInventoryData(prev =>
        prev.map(item =>
          item.id === itemId
            ? { ...item, global_sort_order: newSortOrder }
            : item
        )
      );

      return true;
    } catch (error) {
      console.error('Error updating sort order:', error);
      alert('Error updating sort order. Please try again.');
      return false;
    }
  };

  // Generate sort order between two items
  const generateSortOrder = (prevOrder, nextOrder) => {
    if (!prevOrder && !nextOrder) return 10.0;
    if (!prevOrder) return Math.max(1.0, nextOrder - 10.0);
    if (!nextOrder) return prevOrder + 10.0;
    return (prevOrder + nextOrder) / 2;
  };

  // Handle drag and drop reordering
  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetItem, items) => {
    e.preventDefault();

    if (!draggedItem || draggedItem.id === targetItem.id) {
      setDraggedItem(null);
      return;
    }

    // Find the position where we're dropping
    const targetIndex = items.findIndex(item => item.id === targetItem.id);
    const draggedIndex = items.findIndex(item => item.id === draggedItem.id);

    if (targetIndex === -1 || draggedIndex === -1) return;

    // Calculate new sort order
    let newSortOrder;
    if (targetIndex === 0) {
      // Dropping at the beginning
      const nextOrder = items[0].global_sort_order || 10.0;
      newSortOrder = Math.max(1.0, nextOrder - 10.0);
    } else if (targetIndex === items.length - 1) {
      // Dropping at the end
      const prevOrder = items[items.length - 1].global_sort_order || 10.0;
      newSortOrder = prevOrder + 10.0;
    } else {
      // Dropping in the middle
      const prevOrder = items[targetIndex - 1].global_sort_order || 10.0;
      const nextOrder = items[targetIndex].global_sort_order || 20.0;
      newSortOrder = generateSortOrder(prevOrder, nextOrder);
    }

    // Update the sort order
    const success = await updateSortOrder(draggedItem.id, newSortOrder);
    if (success) {
      // Reload data to reflect new order
      loadInventoryData();
    }

    setDraggedItem(null);
  };

  // Delete selected items
  const deleteSelectedItems = async () => {
    if (selectedItems.size === 0) return;

    const itemCount = selectedItems.size;
    const confirmMessage = itemCount === 1
      ? 'Are you sure you want to delete this item?'
      : `Are you sure you want to delete ${itemCount} items?`;

    if (!confirm(confirmMessage)) return;

    setDeleting(true);
    try {
      const itemIds = Array.from(selectedItems);

      // TODO: Convert to direct fetch for delete operations
      console.log('⚠️ [INVENTORY-VIEWER] Item deletion temporarily disabled to avoid hanging Supabase client');
      console.log('📝 [INVENTORY-VIEWER] Would delete items:', itemIds);

      alert('Item deletion temporarily disabled to avoid hanging database calls.');
      setDeleting(false);
      return;

      // Update local state
      setInventoryData(prev =>
        prev.filter(item => !selectedItems.has(item.id))
      );

      // Clear selection
      setSelectedItems(new Set());

      // Show success message
      alert(`Successfully deleted ${itemCount} item${itemCount > 1 ? 's' : ''}.`);

    } catch (error) {
      console.error('Error deleting items:', error);
      alert('Error deleting items. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const filterData = () => {
    let filtered = [...inventoryData];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.vendor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.supc?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply specific filter
    if (selectedFilter) {
      switch (viewMode) {
        case 'vendor':
          filtered = filtered.filter(item =>
            item.vendor?.toLowerCase().includes(selectedFilter.toLowerCase())
          );
          break;
        case 'category':
          filtered = filtered.filter(item => 
            item.category?.toLowerCase().includes(selectedFilter.toLowerCase())
          );
          break;
        case 'location':
          filtered = filtered.filter(item => 
            item.location?.toLowerCase().includes(selectedFilter.toLowerCase())
          );
          break;
      }
    }

    setFilteredData(filtered);
  };

  const getUniqueValues = (field) => {
    const values = inventoryData
      .map(item => item[field])
      .filter(value => value && value.trim())
      .map(value => value.trim());
    return [...new Set(values)].sort();
  };

  const getGroupedData = () => {
    if (viewMode === 'all') {
      return { 'All Items': filteredData };
    }

    const grouped = {};
    const field = viewMode === 'vendor' ? 'vendor' : viewMode;

    filteredData.forEach(item => {
      const key = item[field] || 'Unassigned';
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(item);
    });

    return grouped;
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    const num = parseFloat(value);
    return isNaN(num) ? value : `$${num.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Download inventory data as CSV
  const downloadInventoryCSV = () => {
    if (!inventoryData || inventoryData.length === 0) {
      alert('No inventory data to download');
      return;
    }

    // Define CSV headers
    const headers = [
      'ID', 'Description', 'Brand', 'Vendor', 'Category', 'Location', 
      'SUPC', 'Case Qty', 'Split Qty', 'Unit', 'Pack Size', 'Cust Number',
      'MFR Number', 'Per LB', 'Case Price', 'Each Price', 'Store ID',
      'Account', 'Store', 'Created At', 'Updated At'
    ];

    // Convert data to CSV format
    const csvContent = [
      headers.join(','),
      ...inventoryData.map(item => [
        item.id || '',
        `"${(item.description || '').replace(/"/g, '""')}"`,
        `"${(item.brand || '').replace(/"/g, '""')}"`,
        `"${(item.vendor || '').replace(/"/g, '""')}"`,
        `"${(item.category || '').replace(/"/g, '""')}"`,
        `"${(item.location || '').replace(/"/g, '""')}"`,
        `"${(item.supc || '').replace(/"/g, '""')}"`,
        item.case_qty || '',
        item.split_qty || '',
        `"${(item.unit || '').replace(/"/g, '""')}"`,
        `"${(item.pack_size || '').replace(/"/g, '""')}"`,
        `"${(item.cust_number || '').replace(/"/g, '""')}"`,
        `"${(item.mfr_number || '').replace(/"/g, '""')}"`,
        item.per_lb || '',
        item.case_price || '',
        item.each_price || '',
        item.store_id || '',
        `"${(item.account || '').replace(/"/g, '""')}"`,
        `"${(item.store || '').replace(/"/g, '""')}"`,
        `"${(item.created_at || '').replace(/"/g, '""')}"`,
        `"${(item.updated_at || '').replace(/"/g, '""')}"`
      ].join(','))
    ].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `inventory_data_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log(`Downloaded ${inventoryData.length} inventory items to CSV`);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading inventory...</p>
      </div>
    );
  }

  const groupedData = getGroupedData();
  const filterOptions = viewMode !== 'all' ? getUniqueValues(viewMode === 'vendor' ? 'vendor' : viewMode) : [];

  return (
    <div className="inventory-viewer">
      <style>{`
        .editable-cell:hover {
          background-color: #f8f9fa !important;
          border: 1px dashed #dee2e6 !important;
        }
        .editable-cell {
          transition: all 0.2s ease;
        }
        .opacity-50 {
          opacity: 0.5;
        }
        .compact-table {
          font-size: 0.75rem;
          table-layout: fixed;
        }
        .compact-table th,
        .compact-table td {
          padding: 0.25rem 0.5rem !important;
          vertical-align: middle;
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .compact-table th {
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .compact-table .editable-cell {
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .description-col {
          width: 22%;
          min-width: 160px;
        }
        .narrow-col {
          width: 7%;
          min-width: 55px;
        }
        .medium-col {
          width: 9%;
          min-width: 70px;
        }
        .checkbox-col {
          width: 30px;
          min-width: 30px;
        }
        .draggable-row {
          transition: background-color 0.2s ease;
        }
        .draggable-row:hover {
          background-color: #f8f9fa !important;
        }
        .draggable-row.dragging {
          opacity: 0.5;
          background-color: #e3f2fd !important;
        }
      `}</style>
      {/* Editing Instructions */}
      {!sortOrderMode ? (
        <div className="alert alert-info mb-4">
          <div className="d-flex align-items-center">
            <i className="feather icon-edit-3 me-2"></i>
            <div>
              <strong>Click to Edit:</strong> Click any field to edit it directly.
              Categories and locations show dropdowns with existing options plus "Add new" at the bottom.
              Press Enter to save or Escape to cancel.
            </div>
          </div>
        </div>
      ) : (
        <div className="alert alert-warning mb-4">
          <div className="d-flex align-items-center">
            <i className="feather icon-move me-2"></i>
            <div>
              <strong>Sort Order Mode:</strong> Drag rows using the ⇅ handle to reorder items within each location.
              This sets the global sort order that will be used when items are added to count sheets.
              The system automatically assigns decimal numbers to allow easy insertion of new items later.
            </div>
          </div>
        </div>
      )}

      {/* View Controls */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row align-items-center">
            {!hideControls && (
              <div className="col-md-4">
                <label className="form-label small">View Mode</label>
                <div className="btn-group w-100" role="group">
                  <button
                    className={`btn btn-sm ${viewMode === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => { setViewMode('all'); setSelectedFilter(''); }}
                  >
                    All Items
                  </button>
                  <button
                    className={`btn btn-sm ${viewMode === 'vendor' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => { setViewMode('vendor'); setSelectedFilter(''); }}
                  >
                    By Vendor
                  </button>
                  <button
                    className={`btn btn-sm ${viewMode === 'category' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => { setViewMode('category'); setSelectedFilter(''); }}
                  >
                    By Category
                  </button>
                  <button
                    className={`btn btn-sm ${viewMode === 'location' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => { setViewMode('location'); setSelectedFilter(''); }}
                  >
                    By Location
                  </button>
                </div>
              </div>
            )}
            
            <div className={hideControls ? "col-md-12" : "col-md-4"}>
              <label className="form-label small">Search</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {(!hideControls && viewMode !== 'all') && (
              <div className="col-md-3">
                <label className="form-label small">Filter by {viewMode}</label>
                <select
                  className="form-control form-control-sm"
                  value={selectedFilter}
                  onChange={(e) => setSelectedFilter(e.target.value)}
                >
                  <option value="">All {viewMode}s</option>
                  {filterOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            )}

            {(!hideControls && viewMode === 'location') && (
              <div className="col-md-1">
                <label className="form-label small">Sort Order</label>
                <button
                  className={`btn btn-sm w-100 ${sortOrderMode ? 'btn-warning' : 'btn-outline-secondary'}`}
                  onClick={() => setSortOrderMode(!sortOrderMode)}
                  title={sortOrderMode ? 'Exit sort mode' : 'Enable sort mode'}
                >
                  {sortOrderMode ? (
                    <>
                      <i className="feather icon-check me-1"></i>
                      Done
                    </>
                  ) : (
                    <>
                      <i className="feather icon-move me-1"></i>
                      Sort
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="row mb-4">
        <div className={hideControls ? "col-md-6" : "col-md-3"}>
          <div className="card">
            <div className="card-body text-center">
              <h4 className="text-primary mb-0">{filteredData.length}</h4>
              <small className="text-muted">Total Items</small>
            </div>
          </div>
        </div>
        {!hideControls && (
          <div className="col-md-3">
            <div className="card">
              <div className="card-body text-center">
                <h4 className="text-success mb-0">{Object.keys(groupedData).length}</h4>
                <small className="text-muted">{viewMode === 'all' ? 'Groups' : `${viewMode}s`}</small>
              </div>
            </div>
          </div>
        )}
        {!hideControls && (
          <div className="col-md-3">
            <div className="card">
              <div className="card-body text-center">
                <h4 className="text-info mb-0">
                  {formatCurrency(
                    filteredData.reduce((sum, item) => {
                      const price = parseFloat(item.case_price) || 0;
                      return sum + price;
                    }, 0)
                  )}
                </h4>
                <small className="text-muted">Total Value</small>
              </div>
            </div>
          </div>
        )}
        {selectedItems.size > 0 && (
          <div className={hideControls ? "col-md-6" : "col-md-3"}>
            <div className="card border-danger">
              <div className="card-body text-center">
                <button
                  className="btn btn-danger btn-sm w-100"
                  onClick={deleteSelectedItems}
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="feather icon-trash-2 me-2"></i>
                      Delete {selectedItems.size} item{selectedItems.size > 1 ? 's' : ''}
                    </>
                  )}
                </button>
                <small className="text-muted d-block mt-1">{selectedItems.size} selected</small>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Inventory Groups */}
      {Object.keys(groupedData).length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="feather icon-package text-muted" style={{fontSize: '3rem'}}></i>
            <h6 className="text-muted mt-3 mb-2">No inventory items found</h6>
            <p className="text-muted">Try adjusting your search or filters, or add some items to get started.</p>
          </div>
        </div>
      ) : (
        Object.entries(groupedData).map(([groupName, items]) => (
          <div key={groupName} className="card mb-4">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center" style={{ flexWrap: 'nowrap', gap: '0.75rem' }}>
                <h6 className="mb-0 text-truncate" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }} title={groupName}>{groupName}</h6>
                <div className="d-flex align-items-center gap-2" style={{ flexWrap: 'nowrap', flexShrink: 0 }}>
                  {viewMode === 'location' && sortOrderMode && items.some(item => !item.global_sort_order) && (
                    <button
                      className="btn btn-sm btn-outline-warning"
                      onClick={() => initializeSortOrders(items)}
                      title="Set initial sort order for items without one"
                    >
                      <i className="feather icon-hash me-1"></i>
                      Init Sort
                    </button>
                  )}
                  <span className="badge bg-primary">{items.length} items</span>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0 compact-table">
                  <thead className="table-light">
                    <tr>
                      <th className="checkbox-col">
                        {viewMode === 'location' && items.length > 1 && (
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={items.every(item => selectedItems.has(item.id))}
                            onChange={() => toggleSelectAll(items)}
                            title="Select all items in this location"
                          />
                        )}
                      </th>
                      {sortOrderMode && viewMode === 'location' && (
                        <th className="narrow-col" title="Drag to reorder">⇅</th>
                      )}
                      <th className="description-col">Description</th>
                      <th className="medium-col">Brand</th>
                      <th className="narrow-col">Pack/Size</th>
                      <th className="narrow-col">Unit</th>
                      <th className="narrow-col">Case Qty</th>
                      <th className="narrow-col">Split Qty</th>
                      <th className="medium-col">Category</th>
                      {viewMode !== 'location' && <th className="medium-col">Location</th>}
                      <th className="medium-col">Vendor</th>
                      <th className="narrow-col">SUPC</th>
                      <th className="narrow-col">Case $</th>
                      <th className="narrow-col">Unit Price</th>
                      <th className="narrow-col">Added</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => {
                      const isEditing = (field) => editingCell?.itemId === item.id && editingCell?.field === field;

                      return (
                        <tr
                          key={item.id || index}
                          className={`${saving ? 'opacity-50' : ''} ${sortOrderMode && viewMode === 'location' ? 'draggable-row' : ''}`}
                          draggable={sortOrderMode && viewMode === 'location'}
                          onDragStart={(e) => sortOrderMode && viewMode === 'location' && handleDragStart(e, item)}
                          onDragOver={(e) => sortOrderMode && viewMode === 'location' && handleDragOver(e)}
                          onDrop={(e) => sortOrderMode && viewMode === 'location' && handleDrop(e, item, items)}
                          style={{
                            cursor: sortOrderMode && viewMode === 'location' ? 'move' : 'default',
                            backgroundColor: draggedItem?.id === item.id ? '#f8f9fa' : 'transparent'
                          }}
                        >
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedItems.has(item.id)}
                              onChange={() => toggleItemSelection(item.id)}
                              title="Select this item"
                            />
                          </td>
                          {sortOrderMode && viewMode === 'location' && (
                            <td>
                              <div className="d-flex align-items-center justify-content-center">
                                <i className="feather icon-move text-muted" style={{fontSize: '1rem', cursor: 'move'}} title="Drag to reorder"></i>
                              </div>
                            </td>
                          )}
                          <td>
                            <strong>
                              {renderEditableCell(
                                item,
                                'description',
                                item.description,
                                isEditing('description')
                              )}
                            </strong>
                          </td>
                          <td>
                            {renderEditableCell(
                              item,
                              'brand',
                              item.brand,
                              isEditing('brand')
                            )}
                          </td>
                          <td>
                            {renderEditableCell(
                              item,
                              'pack_size',
                              item.pack_size,
                              isEditing('pack_size')
                            )}
                          </td>
                          <td>
                            {renderEditableCell(
                              item,
                              'unit',
                              item.unit,
                              isEditing('unit')
                            )}
                          </td>
                          <td>
                            {renderEditableCell(
                              item,
                              'case_qty',
                              item.case_qty,
                              isEditing('case_qty')
                            )}
                          </td>
                          <td>
                            {renderEditableCell(
                              item,
                              'split_qty',
                              item.split_qty,
                              isEditing('split_qty')
                            )}
                          </td>
                          <td>
                            {isEditing('category') ? (
                              renderEditableCell(item, 'category', item.category, true)
                            ) : (
                              <span
                                onClick={() => startEditing(item.id, 'category', item.category)}
                                className="badge bg-secondary editable-cell"
                                style={{ cursor: 'pointer', fontSize: '0.65rem', padding: '0.2rem 0.4rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', display: 'inline-block' }}
                                title={item.category || 'No category'}
                              >
                                {item.category || 'No category'}
                              </span>
                            )}
                          </td>
                          {viewMode !== 'location' && (
                            <td>
                              {isEditing('location') ? (
                                renderEditableCell(item, 'location', item.location, true)
                              ) : (
                                <span
                                  onClick={() => startEditing(item.id, 'location', item.location)}
                                  className="badge bg-info editable-cell"
                                  style={{ cursor: 'pointer', fontSize: '0.65rem', padding: '0.2rem 0.4rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', display: 'inline-block' }}
                                  title={item.location || 'No location'}
                                >
                                  {item.location || 'No location'}
                                </span>
                              )}
                            </td>
                          )}
                          <td>
                            {renderEditableCell(
                              item,
                              'vendor',
                              item.vendor,
                              isEditing('vendor')
                            )}
                          </td>
                          <td>
                            <code>
                              {renderEditableCell(
                                item,
                                'supc',
                                item.supc,
                                isEditing('supc')
                              )}
                            </code>
                          </td>
                          <td>
                            {renderEditableCell(
                              item,
                              'case_price',
                              formatCurrency(item.case_price),
                              isEditing('case_price')
                            )}
                          </td>
                          <td>
                            {renderEditableCell(
                              item,
                              'each_price',
                              formatCurrency(item.each_price),
                              isEditing('each_price')
                            )}
                          </td>
                          <td className="text-muted small">{formatDate(item.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Download CSV Button */}
              <div className="mt-3 text-center">
                <button 
                  className="btn btn-success"
                  onClick={downloadInventoryCSV}
                  disabled={!inventoryData || inventoryData.length === 0}
                >
                  <i className="feather icon-download mr-2"></i>
                  Download to CSV ({inventoryData?.length || 0} items)
                </button>
                <div className="mt-2 text-muted small">
                  This will download all {inventoryData?.length || 0} items currently loaded in the viewer
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default InventoryViewer;
